import PropTypes from 'prop-types';
import React from 'react';
import round from 'lodash/round';
import classNames from 'classnames';
import { getTop3Swells } from '@surfline/web-common/dist/utils/swells';
import cardinal from '@surfline/web-common/dist/utils/mapDegreesCardinal';
import { roundSurfHeight } from '@surfline/web-common/dist/utils/roundUnits';
import swellHeightClassesFromUnits from '../../common/swellHeightClassesFromUnits';
import unitsPropType from '../../propTypes/units';
import waveTimestepPropType from '../../propTypes/waveTimestep';
import windTimestepPropType from '../../propTypes/windTimestep';
import highlightTypePropType from '../../propTypes/forecastTableHighlightType';
import ChevronBox from '../ChevronBox';

const timestepClassName = (isDay, expanded) =>
  classNames({
    'sl-forecast-table__timestep': true,
    'sl-forecast-table__timestep--day': isDay,
    'sl-forecast-table__timestep--time': !isDay,
    'sl-forecast-table__timestep--expanded': expanded,
  });

const swellHeightClass = (height, swellHeightClasses) =>
  swellHeightClasses.find(max => height < max) || swellHeightClasses[swellHeightClasses.length - 1];

const timestepCellClassName = (highlightType, score, swellHeight, units, swellHeightClasses) => {
  const className = {
    'sl-forecast-table__cell': true,
  };

  switch (highlightType) {
    case 'OPTIMAL':
      className['sl-forecast-table__cell--optimal'] = score === 2;
      className['sl-forecast-table__cell--good'] = score === 1;
      break;
    case 'SWELL_HEIGHT': {
      const height = `${swellHeightClass(swellHeight, swellHeightClasses)
        .toString()
        .replace('.', '_')}-${units.waveHeight.toLowerCase()}`;
      className[`sl-forecast-table__cell--swell-${height}`] = true;
      break;
    }
    default:
      break;
  }
  return classNames(className);
};

const headerColSpan = (isExpandedDay, filterTop3Swells, hideSurfAndWind) => {
  if (!isExpandedDay) return 0;
  return 4 + 3 * !filterTop3Swells + 2 * !hideSurfAndWind;
};

const ForecastTableTimestep = ({
  units,
  label,
  wave,
  wind,
  isDay,
  expanded,
  hideSurfAndWind,
  filterTop3Swells,
  onClick,
  highlightType,
}) => {
  const swellScore = Math.max(...wave.swells.map(swell => swell.optimalScore));
  const combinedScore = wind.optimalScore === 2 && swellScore === 2 ? 2 : 0;
  const swells = filterTop3Swells ? getTop3Swells(wave.swells) : wave.swells;
  const swellHeightClasses = swellHeightClassesFromUnits(units.waveHeight);
  return (
    <tr
      className={timestepClassName(isDay, expanded)}
      onClick={onClick}
      onKeyPress={({ charCode }) => {
        if (charCode === 13) onClick();
      }}
      tabIndex={onClick ? 0 : null}
    >
      <th
        className={timestepCellClassName(
          highlightType !== 'SWELL_HEIGHT' ? highlightType : null,
          combinedScore
        )}
        scope="row"
        colSpan={headerColSpan(isDay && expanded, filterTop3Swells, hideSurfAndWind)}
      >
        {isDay ? <ChevronBox direction={expanded ? 'down' : 'right'} /> : null}
        {label}
      </th>
      {!(isDay && expanded) && !hideSurfAndWind ? (
        <td className={timestepCellClassName()}>
          {roundSurfHeight(wave.surf.min, units.waveHeight)}-
          {roundSurfHeight(wave.surf.max, units.waveHeight)}
          <span className="sl-forecast-table__units">{units.waveHeight}</span>
        </td>
      ) : null}
      {!(isDay && expanded) && !hideSurfAndWind ? (
        /* TODO: upgrade react to v16 for `<Fragment>`s */
        <td className={timestepCellClassName(highlightType, wind.optimalScore)}>
          {round(wind.speed)}
          <span className="sl-forecast-table__units">{units.windSpeed}</span>
          &nbsp;
          <span className="sl-forecast-table__direction">
            {cardinal(wind.direction)}({round(wind.direction)}
            º)
          </span>
        </td>
      ) : null}
      {!(isDay && expanded) &&
        swells.map(swell => (
          <td
            key={swell.index}
            className={timestepCellClassName(
              highlightType,
              swell.optimalScore,
              swell.height,
              units,
              swellHeightClasses
            )}
          >
            {round(swell.height, 1) > 0 ? (
              <div>
                {round(swell.height, 1)}
                <span className="sl-forecast-table__units">{units.waveHeight}</span>
                {swell.period}
                s&nbsp;
                <span className="sl-forecast-table__direction">
                  {cardinal(swell.direction)}({round(swell.direction)}
                  º)
                </span>
              </div>
            ) : (
              '-'
            )}
          </td>
        ))}
    </tr>
  );
};

ForecastTableTimestep.propTypes = {
  units: unitsPropType.isRequired,
  label: PropTypes.string.isRequired,
  wave: waveTimestepPropType.isRequired,
  wind: windTimestepPropType.isRequired,
  isDay: PropTypes.bool,
  expanded: PropTypes.bool,
  hideSurfAndWind: PropTypes.bool,
  filterTop3Swells: PropTypes.bool,
  onClick: PropTypes.func,
  highlightType: highlightTypePropType.isRequired,
};

ForecastTableTimestep.defaultProps = {
  isDay: false,
  expanded: false,
  hideSurfAndWind: false,
  filterTop3Swells: false,
  onClick: null,
};

export default ForecastTableTimestep;
